/* eslint-disable max-lines-per-function */
import { updateMFAPreference, verifyTOTPSetup } from 'aws-amplify/auth';
import { QRCodeCanvas } from 'qrcode.react';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Button } from '@e2n/ui';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { CopyToClipboard } from '@/components';
import { FieldBase, useAppForm } from '@/components/form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

export function TOTPSetupForm({
  setIsMFAEnabled,
  totpSetup,
}: {
  setIsMFAEnabled: Dispatch<SetStateAction<boolean>>;
  totpSetup: {
    sharedSecret: string;
    setUpURI: URL;
  };
}) {
  const [isQRCodeVisible, setIsQRCodeVisible] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success'>('idle');
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      z.object({
        challengeAnswer: z
          .string()
          .min(1, {
            message: t(
              'pages.profile.sections.security.setupTOTPForm.codeIsRequired',
            ),
          })
          .refine(
            (value) => /^[0-9]+$/.test(value),
            t(
              'pages.profile.sections.security.setupTOTPForm.codePatternMismatch',
            ),
          ),
      }),
    [t],
  );

  const form = useAppForm({
    defaultValues: { challengeAnswer: '' },
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await verifyTOTPSetup({ code: value.challengeAnswer });
        await updateMFAPreference({ totp: 'PREFERRED' });
        formApi.resetField('challengeAnswer');
        setSubmitStatus('success');
        setIsMFAEnabled(true);
      } catch (error) {
        Sentry.captureException(error);
        setSubmitStatus('idle');
        formApi.setFieldMeta('challengeAnswer', (meta) => ({
          ...meta,
          errors: [
            {
              message: t(
                'pages.profile.sections.security.setupTOTPForm.softwareTokenException',
              ),
            },
          ],
        }));
      }
    },
  });

  return (
    <>
      <p className="text-sm leading-6 text-neutrals-900">
        {t('pages.profile.sections.security.setupTOTPForm.detailsDescription')}
      </p>
      <h2 className="text-base font-semibold leading-7 text-neutrals-900">
        {t('pages.profile.sections.security.setupTOTPForm.scanQRCodeTitle')}
      </h2>
      <p className="text-sm leading-6 text-neutrals-900">
        {t('pages.profile.sections.security.setupTOTPForm.qrCodeDescription')}
      </p>
      <div className="border border-solid border-neutrals-500 dark:border-neutrals-900 rounded-md p-2 w-fit">
        <input
          className="hidden"
          defaultValue={totpSetup.sharedSecret}
          data-testid="pages-profile-security-totp-secret"
        />
        <QRCodeCanvas value={totpSetup.setUpURI.href} />
      </div>
      <div className="flex gap-2 items-center">
        <p className="text-sm leading-6 text-neutrals-900">
          {t('pages.profile.sections.security.setupTOTPForm.unableToScan')}
        </p>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsQRCodeVisible(!isQRCodeVisible)}>
          {isQRCodeVisible ? 'Ausblenden' : 'Anzeigen'}
        </Button>
      </div>
      {isQRCodeVisible && (
        <CopyToClipboard value={totpSetup.sharedSecret} size="lg" />
      )}
      <h2 className="text-base font-semibold leading-7 text-neutrals-900">
        {t('pages.profile.sections.security.setupTOTPForm.verifyCodeTitle')}
      </h2>
      <form
        className="flex flex-col gap-4"
        id="verifyTOTP"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}>
        <form.AppField name="challengeAnswer">
          {(field) => {
            return (
              <FieldBase className="w-full sm:w-1/2">
                <InputOTP
                  id="totp-code"
                  autoFocus={false}
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={field.state.value}
                  onChange={(value) => {
                    setSubmitStatus('idle');
                    field.setValue(value);
                  }}
                  onBlur={field.handleBlur}
                  data-testid="pages-profile-security-totp-input">
                  <InputOTPGroup className="w-full *:data-[slot=input-otp-slot]:flex-1 *:data-[slot=input-otp-slot]:h-12 *:data-[slot=input-otp-slot]:text-xl *:data-[slot=input-otp-slot]:border-neutrals-300 *:data-[slot=input-otp-slot]:bg-white p-1">
                    <InputOTPSlot
                      index={0}
                      data-testid="pages-profile-security-totp-input-0"
                    />
                    <InputOTPSlot
                      index={1}
                      data-testid="pages-profile-security-totp-input-1"
                    />
                    <InputOTPSlot
                      index={2}
                      data-testid="pages-profile-security-totp-input-2"
                    />
                    <InputOTPSlot
                      index={3}
                      data-testid="pages-profile-security-totp-input-3"
                    />
                    <InputOTPSlot
                      index={4}
                      data-testid="pages-profile-security-totp-input-4"
                    />
                    <InputOTPSlot
                      index={5}
                      data-testid="pages-profile-security-totp-input-5"
                    />
                  </InputOTPGroup>
                </InputOTP>
              </FieldBase>
            );
          }}
        </form.AppField>
        <form.Subscribe
          selector={(state) => ({
            isSubmitting: state.isSubmitting,
          })}>
          {({ isSubmitting }) => (
            <Button
              data-testid="pages-profile-security-totp-verify"
              className="w-fit"
              form="verifyTOTP"
              type="submit"
              disabled={isSubmitting}>
              {isSubmitting
                ? t(
                    'pages.profile.sections.security.setupTOTPForm.verifyButtonLabelIsSubmitting',
                  )
                : submitStatus === 'success'
                  ? t(
                      'pages.profile.sections.security.setupTOTPForm.verifyButtonLabelIsSubmitted',
                    )
                  : t(
                      'pages.profile.sections.security.setupTOTPForm.verifyButtonLabel',
                    )}
            </Button>
          )}
        </form.Subscribe>
      </form>
    </>
  );
}
